import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Cable, CheckCircle2, Gauge, ArrowRight, ChevronRight,
  RefreshCw, BookOpen, BadgeCheck, Users, ArrowUpRight,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, PortalButton } from "@/components/Motion";
import { VehicleCompatibility } from "@/components/VehicleCompatibility";
import { API, PORTAL_URL, SITE_ORIGIN } from "@/lib/site";

const FEATURES = [
  [Cable, "OBD, Bench & Boot", "ECU and TCU reading and writing through the diagnostic port, on the bench, or in boot mode — depending on the supported protocol."],
  [CheckCircle2, "Automatic Checksum Correction", "Checksums are corrected automatically during the writing process on supported families."],
  [Gauge, "Real-Time Datalogging", "Live engine data logging built into the tool for calibration development and verification."],
  [BookOpen, "Guided Instructions", "Step-by-step software guidance and connection instructions for each supported vehicle."],
  [RefreshCw, "Free Software Updates", "Ongoing software and protocol updates are included with the tool."],
  [BadgeCheck, "No Annual Subscription", "AutoTuner operates without a mandatory annual AutoTuner subscription fee."],
];

const METHODS = [
  ["OBD", "Programming through the vehicle's diagnostic connection, where the ECU or TCU protocol supports it. The fastest method for day-to-day tuning work."],
  ["Bench", "Direct connection to the ECU or TCU on the bench, outside the vehicle, without opening the unit — where the applicable protocol supports it."],
  ["Boot", "Direct ECU access used for applicable control units and protocols, typically for recovery or units not covered by OBD or Bench methods."],
];

const AUDIENCE = [
  "Professional ECU tuners",
  "Remapping businesses",
  "Automotive workshops adding ECU tuning",
  "Existing tuning businesses upgrading equipment",
  "Master tuners building a Slave network",
];

const FAQS = [
  ["What is an AutoTuner ECU tuning tool?", "AutoTuner is a professional ECU and TCU programming tool used to read and write engine and gearbox control units. It supports OBD, Bench and Boot programming methods on thousands of vehicles, with guided instructions and built-in datalogging."],
  ["What is the difference between AutoTuner Master and Slave?", "An AutoTuner Master produces unencrypted files — you can edit them yourself with compatible calibration software or send them to any tuning file provider. An AutoTuner Slave reads and writes encrypted files that are processed by the Master or file provider the Slave is linked to."],
  ["Does AutoTuner have an annual subscription?", "No. AutoTuner does not require a mandatory annual subscription — software updates are included with the tool."],
  ["Can AutoTuner read and write ECUs and TCUs?", "Yes. AutoTuner reads and writes both engine control units (ECU) and transmission control units (TCU), subject to protocol coverage for the specific unit."],
  ["Does AutoTuner support OBD, Bench and Boot?", "Yes, all three methods are supported — but not every ECU supports every method. Coverage depends on the specific control unit and protocol; the software tells you which method applies to each vehicle."],
  ["Can I use Regal Tuning files with an AutoTuner Master?", "Yes. Read the original file with your AutoTuner Master and submit it through the Regal Tuning file portal — our calibrators return a professionally developed tuning file. You remain free to use any file provider you choose."],
  ["Can an AutoTuner Master use another tuning file provider?", "Absolutely. File independence is the point of a Master tool — you can edit files yourself with compatible software or work with any tuning file provider. Regal Tuning is one option, never a requirement."],
  ["Can an AutoTuner Slave be upgraded to Master?", "Master and Slave are different licence configurations. If you think you may need Master-level independence later, talk to us before purchasing and we'll recommend the right route."],
  ["Can an AutoTuner Slave change Master?", "A Slave is licensed to the Master it is linked to. Changing to a different Master requires the current Master provider to release the link — contact us and we will advise on the process for your tool."],
  ["Does AutoTuner include tuning or file editing software?", "No. AutoTuner reads and writes ECU/TCU data, but dedicated calibration editing software is a separate product and is not included with the tool."],
];

export default function Autotuner() {
  const [product, setProduct] = useState(null);

  useEffect(() => {
    // canonical is managed globally by ScrollManager in App.js
    const breadcrumb = {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_ORIGIN + "/" },
        { "@type": "ListItem", position: 2, name: "Tuning Tools", item: SITE_ORIGIN + "/tuning-tools" },
        { "@type": "ListItem", position: 3, name: "AutoTuner", item: SITE_ORIGIN + "/autotuner" },
      ],
    };
    const faq = {
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: FAQS.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
    };
    const s1 = document.createElement("script");
    s1.type = "application/ld+json";
    s1.text = JSON.stringify(breadcrumb);
    const s2 = document.createElement("script");
    s2.type = "application/ld+json";
    s2.text = JSON.stringify(faq);
    document.head.appendChild(s1);
    document.head.appendChild(s2);
    return () => { s1.remove(); s2.remove(); };
  }, []);

  useEffect(() => {
    axios.get(`${API}/products/autotuner-master`).then((r) => setProduct(r.data)).catch(() => {});
  }, []);

  const includes = product?.includes || [];

  return (
    <main data-testid="autotuner-page">
      {/* HERO */}
      <section className="relative overflow-hidden pt-36 pb-20 sm:pt-44 sm:pb-24" data-testid="autotuner-hero">
        <div className="absolute inset-0 dot-grid opacity-50" />
        <div className="container-regal relative">
          <nav className="mb-8 flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500" aria-label="Breadcrumb" data-testid="autotuner-breadcrumbs">
            <Link to="/" className="hover:text-regal-amber">Home</Link>
            <ChevronRight size={12} />
            <Link to="/tuning-tools" className="hover:text-regal-amber">Tuning Tools</Link>
            <ChevronRight size={12} />
            <span className="text-neutral-300">AutoTuner</span>
          </nav>
          <Reveal><p className="overline-label mb-4">Genuine AutoTuner Equipment</p></Reveal>
          <Reveal delay={0.08}>
            <h1 className="max-w-4xl font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold uppercase leading-[1.0] tracking-tight text-ink">
              AutoTuner ECU &amp; TCU Tuning Tools
            </h1>
          </Reveal>
          <Reveal delay={0.16} className="mt-7 max-w-2xl">
            <p className="text-base sm:text-lg text-neutral-400 leading-relaxed">
              AutoTuner is a professional ECU and TCU programming tool offering OBD, Bench and Boot methods — available as a Master tool for full file independence, or as a Slave linked to your chosen file provider.
            </p>
          </Reveal>
          <Reveal delay={0.24} className="mt-9 flex flex-wrap gap-4">
            <Link to="/tuning-tools?q=AutoTuner" data-testid="autotuner-shop-cta" className="inline-flex items-center gap-2 bg-regal-amber px-9 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-white hover:bg-amber-400 transition-colors">
              Shop AutoTuner <ArrowRight size={15} />
            </Link>
            <a href="#master-vs-slave" data-testid="autotuner-compare-cta" className="inline-flex items-center gap-2 border border-linebright px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-ink hover:border-regal-amber hover:text-regal-amber transition-colors">
              Master or Slave?
            </a>
          </Reveal>
        </div>
      </section>

      {/* WHY AUTOTUNER */}
      <section className="border-t border-line bg-surface py-20 sm:py-24" data-testid="autotuner-features">
        <div className="container-regal">
          <Reveal><p className="overline-label mb-4">Why AutoTuner</p></Reveal>
          <Reveal delay={0.06}><h2 className="mb-12 max-w-3xl font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink leading-[1.08]">A professional tool, built for daily tuning work</h2></Reveal>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map(([Icon, t, d], i) => (
              <Reveal key={t} delay={i * 0.05}>
                <div className="h-full border border-line bg-white p-7 shadow-[0_1px_2px_rgba(16,24,40,0.05)]">
                  <Icon size={22} className="text-regal-amber" strokeWidth={1.5} />
                  <h3 className="mt-4 font-display text-base font-bold uppercase tracking-wide text-ink">{t}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-neutral-400">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MASTER VS SLAVE */}
      <section id="master-vs-slave" className="border-t border-line py-20 sm:py-24 scroll-mt-24" data-testid="autotuner-compare">
        <div className="container-regal">
          <Reveal><p className="overline-label mb-4">Master vs Slave</p></Reveal>
          <Reveal delay={0.06}><h2 className="mb-12 max-w-3xl font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink leading-[1.08]">Choose the licence that fits your business</h2></Reveal>
          <div className="grid gap-6 lg:grid-cols-2">
            <Reveal>
              <div className="flex h-full flex-col border border-line bg-white p-8 sm:p-10 shadow-[0_1px_2px_rgba(16,24,40,0.05)]" data-testid="compare-master-card">
                <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-regal-amber">Independence</p>
                <h3 className="mt-2 font-display text-2xl font-extrabold uppercase tracking-tight text-ink">AutoTuner Master</h3>
                <p className="mt-4 text-sm sm:text-base leading-relaxed text-neutral-400">
                  AutoTuner Master reads ECUs and TCUs and produces unencrypted files. Modify them yourself using compatible binary editing software, or send them to a tuning file provider of your choice — the file is yours.
                </p>
                <p className="mt-3 text-sm sm:text-base leading-relaxed text-neutral-400">
                  Master is intended for professional tuners who want independence and full control over their tuning files.
                </p>
                <div className="mt-auto pt-8">
                  <Link to="/autotuner-master" data-testid="view-autotuner-master" className="inline-flex items-center gap-2 bg-regal-amber px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-white hover:bg-amber-400 transition-colors">
                    View AutoTuner Master <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="flex h-full flex-col border border-line bg-white p-8 sm:p-10 shadow-[0_1px_2px_rgba(16,24,40,0.05)]" data-testid="compare-slave-card">
                <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-regal-amber">Supported</p>
                <h3 className="mt-2 font-display text-2xl font-extrabold uppercase tracking-tight text-ink">AutoTuner Slave</h3>
                <p className="mt-4 text-sm sm:text-base leading-relaxed text-neutral-400">
                  AutoTuner Slave reads and writes encrypted files that are processed by the Master — the tuning file provider the Slave is linked to.
                </p>
                <p className="mt-3 text-sm sm:text-base leading-relaxed text-neutral-400">
                  This suits workshops and tuners who want professionally developed calibration files without developing the files themselves.
                </p>
                <div className="mt-auto pt-8">
                  <Link to="/product/autotuner-slave" data-testid="view-autotuner-slave" className="inline-flex items-center gap-2 border border-linebright px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-ink hover:border-regal-amber hover:text-regal-amber transition-colors">
                    View AutoTuner Slave <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* OBD / BENCH / BOOT */}
      <section className="border-t border-line bg-surface py-20 sm:py-24" data-testid="autotuner-methods">
        <div className="container-regal">
          <Reveal><p className="overline-label mb-4">Programming Methods</p></Reveal>
          <Reveal delay={0.06}><h2 className="mb-5 max-w-3xl font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink leading-[1.08]">OBD, Bench &amp; Boot explained</h2></Reveal>
          <Reveal delay={0.1}><p className="mb-12 max-w-2xl text-sm sm:text-base text-neutral-400 leading-relaxed">Three ways to reach the control unit. Which method applies depends on the specific ECU or TCU — not every unit supports every method.</p></Reveal>
          <div className="grid gap-px border border-line bg-line sm:grid-cols-3">
            {METHODS.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.07} className="bg-white">
                <div className="h-full p-8">
                  <p className="font-mono text-xs tracking-[0.3em] text-regal-amber">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-4 font-display text-xl font-extrabold uppercase tracking-wide text-ink">{t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-400">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AUTOTUNER VEHICLE COMPATIBILITY */}
      <section id="autotuner-compat" className="border-t border-line py-20 sm:py-24 scroll-mt-24" data-testid="autotuner-compat-section">
        <div className="container-regal">
          <Reveal>
            <VehicleCompatibility
              apiBase="at-vehicles"
              testId="at-compat"
              ctaTo="#master-vs-slave"
              ctaLabel="View AutoTuner Options"
              heading="AutoTuner Vehicle Compatibility"
              intro="Check whether your vehicle, ECU or TCU is supported by AutoTuner — search by make, model, engine or ECU and see the available connection methods (OBD, Bench, Boot). Compatibility data comes from the official AutoTuner vehicle database; exact coverage depends on the control unit and your Master or Slave software."
            />
          </Reveal>
        </div>
      </section>

      {/* FILE SUPPORT */}
      <section className="border-t border-line py-20 sm:py-24" data-testid="autotuner-file-support">
        <div className="container-regal grid items-center gap-12 lg:grid-cols-2">
          <div>
            <Reveal><p className="overline-label mb-4">Regal Tuning File Service</p></Reveal>
            <Reveal delay={0.06}>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink leading-[1.08]">More than hardware — professional tuning file support</h2>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-5 text-sm sm:text-base text-neutral-400 leading-relaxed">
                Regal Tuning doesn't simply sell tools. We operate a professional <Link to="/tuning-files" className="text-regal-amber underline-offset-4 hover:underline">ECU tuning file service</Link> for tuning businesses and workshops worldwide — developed in-house and verified on our 4WD dyno.
              </p>
              <p className="mt-4 text-sm sm:text-base text-neutral-400 leading-relaxed">
                With an AutoTuner Master, you can read an ECU or TCU and submit the original file to Regal Tuning for calibration through our file portal. And because Master files are unencrypted, you keep the freedom to use compatible editing software or any other tuning-file provider — Regal Tuning is a choice, never a lock-in.
              </p>
            </Reveal>
            <Reveal delay={0.18} className="mt-8 flex flex-wrap items-center gap-4">
              <PortalButton label="Upload a File" testId="autotuner-upload-cta" />
              <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-regal-amber underline-offset-4 hover:underline" data-testid="autotuner-portal-link">
                Existing customer? File Portal <ArrowUpRight className="inline" size={13} />
              </a>
            </Reveal>
          </div>
          <Reveal delay={0.12}>
            <div className="overflow-hidden border border-line">
              <img src="/assets/workshop/dyno-turbos.webp" alt="Porsche Turbo S on the Regal Tuning 4WD dyno during calibration development" loading="lazy" className="aspect-[4/3] w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="border-t border-line bg-surface py-20 sm:py-24" data-testid="autotuner-included">
        <div className="container-regal max-w-4xl">
          <Reveal><p className="overline-label mb-4">In The Box</p></Reveal>
          <Reveal delay={0.06}><h2 className="mb-10 font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink leading-[1.08]">What is included with AutoTuner?</h2></Reveal>
          {includes.length > 0 ? (
            <Reveal delay={0.1}>
              <ul className="grid gap-3 sm:grid-cols-2" data-testid="autotuner-includes-list">
                {includes.map((inc) => (
                  <li key={inc} className="flex items-start gap-3 border border-line bg-white p-4 text-sm text-neutral-300">
                    <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-regal-amber" /> {inc}
                  </li>
                ))}
              </ul>
              <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.18em] text-neutral-500">Box contents per the current AutoTuner Master listing — confirm at order time</p>
            </Reveal>
          ) : (
            <p className="border border-dashed border-linebright p-8 text-sm text-neutral-400" data-testid="autotuner-includes-pending">Box contents for this product are being confirmed — contact our team for the exact package contents before ordering.</p>
          )}
        </div>
      </section>

      {/* WHO IS IT FOR */}
      <section className="border-t border-line py-20 sm:py-24" data-testid="autotuner-audience">
        <div className="container-regal max-w-4xl">
          <Reveal><p className="overline-label mb-4">Who It's For</p></Reveal>
          <Reveal delay={0.06}><h2 className="mb-10 font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink leading-[1.08]">Who is AutoTuner for?</h2></Reveal>
          <div className="grid gap-3 sm:grid-cols-2">
            {AUDIENCE.map((a, i) => (
              <Reveal key={a} delay={i * 0.04}>
                <p className="flex items-center gap-3 border border-line bg-white p-4 text-sm font-medium text-ink">
                  <Users size={16} className="shrink-0 text-regal-amber" /> {a}
                </p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-8 text-sm leading-relaxed text-neutral-400">
              Professional equipment supports professional work — the tool alone doesn't replace tuning knowledge and experience. If you're new to ECU tuning, talk to us first: our <Link to="/become-a-dealer" className="text-regal-amber underline-offset-4 hover:underline">dealer programme</Link> includes setup, guidance and ongoing file support.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-line bg-surface py-20 sm:py-24" data-testid="autotuner-faq">
        <div className="container-regal max-w-4xl">
          <Reveal><p className="overline-label mb-4">FAQ</p></Reveal>
          <Reveal delay={0.06}><h2 className="mb-10 font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink leading-[1.08]">AutoTuner questions, answered</h2></Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="w-full" data-testid="autotuner-faq-accordion">
              {FAQS.map(([q, a], i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-line" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-display text-sm sm:text-base font-semibold text-ink hover:text-regal-amber hover:no-underline">{q}</AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-neutral-400">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-10 text-sm text-neutral-400">
              Ready to order? Browse the <Link to="/tuning-tools" className="text-regal-amber underline-offset-4 hover:underline">full tuning tools range</Link> or go straight to the <Link to="/autotuner-master" className="text-regal-amber underline-offset-4 hover:underline">AutoTuner Master</Link>.
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
